import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { format, subDays, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const StudentAttendanceSummary = () => {
  const { t, i18n } = useTranslation('studentattendancesummary');
  const isRTL = i18n.language === 'ar';
  
  // Router and Auth hooks
  const { api, user } = useAuth();
  const { studentId } = useParams();
  const navigate = useNavigate();
  
  // State variables
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Separate state for form inputs vs actual query parameters
  const [dateInputs, setDateInputs] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [queryDates, setQueryDates] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [viewMode, setViewMode] = useState('summary'); // 'summary', 'calendar', 'details'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'present', 'absent', 'late'

  // Handle date input changes without triggering API call
  const handleDateChange = (e, field) => {
    setDateInputs({
      ...dateInputs,
      [field]: e.target.value
    });
  };

  // Handle form submission to update query parameters
  const handleDateSubmit = (e) => {
    e.preventDefault();
    setQueryDates({
      startDate: dateInputs.startDate,
      endDate: dateInputs.endDate
    });
  };

  // Reset to last 30 days
  const handleReset = () => {
    const newDates = {
      startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd')
    };
    setDateInputs(newDates);
    setQueryDates(newDates);
  };

  // Fetch student attendance data when query dates change
  useEffect(() => {
    const fetchAttendanceData = async () => {
      setLoading(true);
      try {
        const response = await api.get(
          `/api/v1/attendance/schools/${user.school_reg}/attendance/student/${studentId}`,
          { params: { start_date: queryDates.startDate, end_date: queryDates.endDate } }
        );
        setStudentData(response.data);
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchFailed'));
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [api, studentId, user.school_reg, queryDates, t]);

  // If we're loading or there's an error, show appropriate UI
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">{t('errors.title')}</strong>
          <span className={`block sm:inline ${isRTL ? 'mr-2' : 'ml-2'}`}> {error}</span>
        </div>
      </div>
    );
  }

  // If we have no data after loading
  if (!studentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">{t('errors.noData')}</div>
      </div>
    );
  }

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'absent': return 'bg-red-500';
      case 'late': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'present': return 'text-green-600';
      case 'absent': return 'text-red-600';
      case 'late': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100';
      case 'absent': return 'bg-red-100';
      case 'late': return 'bg-yellow-100';
      default: return 'bg-gray-100';
    }
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    let totalSessions = 0;
    let totalAttended = 0;
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;

    studentData.attendance_records.forEach(record => {
      totalSessions += record.total_sessions;
      totalAttended += record.sessions_attended;
      
      record.sessions.forEach(session => {
        if (session.status === 'present') presentCount++;
        else if (session.status === 'absent') absentCount++;
        else if (session.status === 'late') lateCount++;
      });
    });

    return {
      totalSessions,
      totalAttended,
      attendanceRate: totalSessions > 0 ? (totalAttended / totalSessions) * 100 : 0,
      presentCount,
      absentCount,
      lateCount
    };
  };

  const summary = calculateSummary();

  // Prepare chart data
  const prepareChartData = () => {
    // Weekly attendance rate
    const weeklyData = [];
    const records = [...studentData.attendance_records].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    let currentWeekStart = null;
    let weekSessions = 0;
    let weekAttended = 0;

    records.forEach(record => {
      const recordDate = new Date(record.date);
      const weekStart = new Date(recordDate);
      weekStart.setDate(recordDate.getDate() - recordDate.getDay());
      
      if (!currentWeekStart || weekStart.getTime() !== currentWeekStart.getTime()) {
        if (currentWeekStart) {
          const rate = weekSessions > 0 ? (weekAttended / weekSessions) * 100 : 0;
          weeklyData.push({
            week: format(currentWeekStart, 'MMM d'),
            rate: Math.round(rate)
          });
        }
        currentWeekStart = weekStart;
        weekSessions = 0;
        weekAttended = 0;
      }
      
      weekSessions += record.total_sessions;
      weekAttended += record.sessions_attended;
    });

    // Add the last week
    if (currentWeekStart) {
      const rate = weekSessions > 0 ? (weekAttended / weekSessions) * 100 : 0;
      weeklyData.push({
        week: format(currentWeekStart, 'MMM d'),
        rate: Math.round(rate)
      });
    }

    // Status distribution for pie chart
    const pieData = [
      { name: t('status.present'), value: summary.presentCount, color: '#10B981' },
      { name: t('status.late'), value: summary.lateCount, color: '#F59E0B' },
      { name: t('status.absent'), value: summary.absentCount, color: '#EF4444' }
    ];

    return { weeklyData, pieData };
  };

  const chartData = prepareChartData();

  // Filter records based on selected status
  const filteredRecords = studentData.attendance_records.filter(record => {
    if (filterStatus === 'all') return true;
    return record.sessions.some(session => session.status === filterStatus);
  });

  // Render date range selector with submit button
  const renderDateSelector = () => (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <h3 className={`text-lg font-medium text-gray-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
        {t('dateRange.title')}
      </h3>
      <form onSubmit={handleDateSubmit} className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
        <div className="flex-1">
          <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('dateRange.startDate')}
          </label>
          <input
            type="date"
            value={dateInputs.startDate}
            onChange={(e) => handleDateChange(e, 'startDate')}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${isRTL ? 'text-right' : 'text-left'}`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
        <div className="flex-1">
          <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('dateRange.endDate')}
          </label>
          <input
            type="date"
            value={dateInputs.endDate}
            onChange={(e) => handleDateChange(e, 'endDate')}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${isRTL ? 'text-right' : 'text-left'}`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
        <div className={`flex items-end space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t('dateRange.apply')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
          >
            {t('dateRange.last30Days')}
          </button>
        </div>
      </form>
    </div>
  );

  // Render summary cards
  const renderSummaryCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className={`text-xl font-semibold text-gray-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('summary.overallAttendance')}
        </h3>
        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="text-4xl font-bold text-blue-600">{Math.round(studentData.overall_attendance_rate)}%</div>
          <div className={`text-sm text-gray-500 ${isRTL ? 'mr-2' : 'ml-2'}`}>
            ({summary.totalAttended}/{summary.totalSessions} {t('summary.sessions')})
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${Math.round(studentData.overall_attendance_rate)}%` }}
          ></div>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className={`text-xl font-semibold text-gray-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('summary.statusBreakdown')}
        </h3>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{summary.presentCount}</div>
            <div className="text-sm text-gray-500">{t('status.present')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-500">{summary.lateCount}</div>
            <div className="text-sm text-gray-500">{t('status.late')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-500">{summary.absentCount}</div>
            <div className="text-sm text-gray-500">{t('status.absent')}</div>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className={`text-xl font-semibold text-gray-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('summary.studentInfo')}
        </h3>
        <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`}>
          <div className="mb-1">
            <span className="font-medium">{t('summary.name')}:</span> {studentData.student_name}
          </div>
          <div>
            <span className="font-medium">{t('summary.class')}:</span> {studentData.attendance_records[0]?.sessions[0]?.class_id || 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );

  // Render view mode tabs
  const renderViewTabs = () => (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className={`flex border-b ${isRTL ? 'flex-row-reverse' : ''}`}>
        <button
          onClick={() => setViewMode('summary')}
          className={`flex-1 py-3 px-4 text-center font-medium ${viewMode === 'summary' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`}
        >
          {t('tabs.summary')}
        </button>
        <button
          onClick={() => setViewMode('calendar')}
          className={`flex-1 py-3 px-4 text-center font-medium ${viewMode === 'calendar' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`}
        >
          {t('tabs.calendar')}
        </button>
        <button
          onClick={() => setViewMode('details')}
          className={`flex-1 py-3 px-4 text-center font-medium ${viewMode === 'details' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`}
        >
          {t('tabs.details')}
        </button>
      </div>
    </div>
  );

  // Render charts for summary view
  const renderCharts = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className={`text-lg font-medium text-gray-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('charts.weeklyAttendance')}
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData.weeklyData}
              margin={{ 
                top: 5, 
                right: isRTL ? 5 : 30, 
                left: isRTL ? 30 : 20, 
                bottom: 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis domain={[0, 100]} />
              <Tooltip formatter={(value) => [`${value}%`, t('charts.attendanceRate')]} />
              <Bar dataKey="rate" fill="#3B82F6" name={t('charts.attendanceRate')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className={`text-lg font-medium text-gray-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('charts.statusDistribution')}
        </h3>
        <div className="h-64 flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData.pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [value, t('summary.sessions')]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  // Render calendar view
  const renderCalendarView = () => {
    const records = studentData.attendance_records.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className={`text-lg font-medium text-gray-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('calendar.title')}
        </h3>
        
        <div className={`flex mb-4 overflow-x-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 ${isRTL ? 'ml-2' : 'mr-2'} rounded-full ${filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {t('filter.all')}
          </button>
          <button
            onClick={() => setFilterStatus('present')}
            className={`px-3 py-1 ${isRTL ? 'ml-2' : 'mr-2'} rounded-full ${filterStatus === 'present' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}
          >
            {t('status.present')}
          </button>
          <button
            onClick={() => setFilterStatus('late')}
            className={`px-3 py-1 ${isRTL ? 'ml-2' : 'mr-2'} rounded-full ${filterStatus === 'late' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700'}`}
          >
            {t('status.late')}
          </button>
          <button
            onClick={() => setFilterStatus('absent')}
            className={`px-3 py-1 ${isRTL ? 'ml-2' : 'mr-2'} rounded-full ${filterStatus === 'absent' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700'}`}
          >
            {t('status.absent')}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecords.map((record, idx) => (
            <div key={idx} className="border rounded-lg overflow-hidden">
              <div className={`bg-gray-100 px-4 py-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {format(parseISO(record.date), 'EEEE, MMM d, yyyy')}
              </div>
              <div className="p-3">
                <div className={`text-sm text-gray-500 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('calendar.attendanceRate')}: {record.attendance_rate}%
                </div>
                {record.sessions.map((session, sessionIdx) => (
                  <div 
                    key={sessionIdx} 
                    className={`mb-2 p-2 rounded-md ${getStatusBgColor(session.status)}`}
                  >
                    <div className={`flex justify-between mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-medium">{session.session_name.trim()}</span>
                      <span className={`font-medium ${getStatusTextColor(session.status)}`}>
                        {t(`status.${session.status}`)}
                      </span>
                    </div>
                    <div className={`text-sm text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('calendar.time')}: {session.time}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render detailed log view
  const renderDetailedView = () => {
    const records = [...studentData.attendance_records].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    
    return (
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="p-4 border-b">
          <h3 className={`text-lg font-medium text-gray-800 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('detailedLog.title')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y divide-gray-200 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('detailedLog.date')}
                </th>
                <th scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('detailedLog.session')}
                </th>
                <th scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('detailedLog.time')}
                </th>
                <th scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('detailedLog.status')}
                </th>
                <th scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('detailedLog.remarks')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.flatMap((record, idx) => 
                record.sessions.map((session, sessionIdx) => (
                  <tr key={`${idx}-${sessionIdx}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(parseISO(record.date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.session_name.trim()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBgColor(session.status)} ${getStatusTextColor(session.status)}`}>
                        {t(`status.${session.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.remarks || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={`container mx-auto max-w-7xl ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <h1 className={`text-2xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('title')}
        </h1>
        <button 
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
        >
          {t('back')}
        </button>
      </div>
      
      {renderDateSelector()}
      {renderSummaryCards()}
      {renderViewTabs()}
      
      {viewMode === 'summary' && renderCharts()}
      {viewMode === 'calendar' && renderCalendarView()}
      {viewMode === 'details' && renderDetailedView()}
    </div>
  );
};

export default StudentAttendanceSummary;